import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "@i18n";
import IconComponent from "@CubeComponents/icons";
import MenuComponent from "@CubeComponents/html/MenuComponent";
import MenuItemComponent from "@CubeComponents/html/MenuItemComponent";
import UserNameTagComponent from "../../../cubeComponent/html/UserNameTagComponent";
import { uploadStringToTemp } from "@Services/tempUpload";
import { usePortalChat, messageTime, dayLabel } from "./context";
import { useChatListModelContext } from "./listModelContext";
import { useChatComposer, type PendingAttachment } from "../core/useChatComposer";
import {
  systemLineText,
  canEditMessage,
  everyoneReadUpTo,
  readerCountFor,
  tickStateFor,
  isGroupedWithPrevious,
  showDaySeparator,
  typingLineText,
  conversationSubtitle,
  formatFileSize,
  isImageAttachment,
} from "../core/chatThreadModel";
import {
  MESSAGE_TEXT_MAX_LENGTH,
  HIGHLIGHT_FLASH_MS,
  HIGHLIGHT_FETCH_BACK_MAX_PAGES,
  ATTACHMENT_CARD_WIDTH,
  ATTACHMENT_PREVIEW_HEIGHT,
} from "../portalChat.constants";
import type { ChatMessage } from "../api/portalChat.api";
import { EmojiPicker } from "./EmojiPicker";
import { ConversationAvatar } from "./ConversationAvatar";
import { MembersDialog, AttachmentsDialog, RenameDialog } from "./ThreadDialogs";
import { HideConfirmDialog } from "./ChatListDialogs";

type MessageAttachment = NonNullable<ChatMessage["attachments"]>[number];

function SystemLine({ message, selfId }: { readonly message: ChatMessage; readonly selfId: string }) {
  const { t } = useTranslation();
  // PRD §4.3 audience-aware wording lives in the shared thread model (mobile renders it too).
  const text = systemLineText(message, selfId, t);
  return (
    <div className="my-2 flex justify-center">
      <span className="rounded-full bg-[#EEF2F5] px-3 py-0.5 text-xs text-[#6B757D]">{text}</span>
    </div>
  );
}

/** Centered empty block for the thread side (design "empty view"): icon disc, title, body. */
function ThreadEmpty({ title, body }: { readonly title: string; readonly body?: string | undefined }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col items-center justify-center px-6 text-center">
      <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#E4F1F9]">
        <IconComponent name="Chat" width="1.5rem" height="1.5rem" />
      </span>
      <div className="text-[0.9375rem] font-bold text-[#1C2228]">{title}</div>
      {body && <div className="mt-1.5 max-w-[18rem] text-xs leading-relaxed text-[#6B757D]">{body}</div>}
    </div>
  );
}

/** Right pane: thread header, message history, and the composer. */
function ChatThreadPane() {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const listModel = useChatListModelContext();
  const conversation = chat.conversations.find((c) => c._id === chat.selectedId) ?? null;
  const thread = chat.thread;

  const [menuFor, setMenuFor] = useState<{ message: ChatMessage; anchor: HTMLElement } | null>(null);
  const [emojiAnchor, setEmojiAnchor] = useState<{ anchor: HTMLElement; messageId: string } | null>(null);
  const [attachMenuAnchor, setAttachMenuAnchor] = useState<HTMLElement | null>(null);
  const [showMembers, setShowMembers] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [threadMenuAnchor, setThreadMenuAnchor] = useState<HTMLElement | null>(null);
  const [hideCandidate, setHideCandidate] = useState(false);
  const [composerFocused, setComposerFocused] = useState(false);

  // PRD §4.6: open menus/popovers close when the panel is maximised or restored.
  useEffect(() => {
    setMenuFor(null);
    setEmojiAnchor(null);
    setAttachMenuAnchor(null);
    setThreadMenuAnchor(null);
  }, [chat.menuEpoch]);

  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const stickToBottomRef = useRef(true);

  // The composer state machine (draft, uploads, mentions, typing throttle, failed-send) is the
  // shared core; this pane supplies the web uploader and re-arms stick-to-bottom on send.
  const {
    text,
    pending,
    showMentionPicker,
    setShowMentionPicker,
    sending,
    uploading,
    failed,
    failedSend,
    addFiles,
    retryUpload,
    removePending,
    removeAllPending,
    handleSend,
    retryFailedSend,
    handleTextChange,
    insertMention,
    insertText,
    dismissFailedSend,
  } = useChatComposer<File>({
    conversationId: chat.selectedId,
    memberCount: chat.thread?.members.length ?? 0,
    upload: async (file) => (await uploadStringToTemp(file, file.name)) ?? null,
    onSendStarted: () => {
      stickToBottomRef.current = true;
    },
  });

  // Local thumbnails for staged images. Object URLs are minted once per pending row and revoked
  // when the row leaves the bar (and on unmount) so a long session never leaks blobs.
  const [previewUrls, setPreviewUrls] = useState<Map<string, string>>(() => new Map());
  const previewUrlsRef = useRef(previewUrls);
  previewUrlsRef.current = previewUrls;
  useEffect(() => {
    setPreviewUrls((prev) => {
      const next = new Map(prev);
      let changed = false;
      const live = new Set(pending.map((item) => item.id));
      for (const [id, url] of next) {
        if (!live.has(id)) {
          URL.revokeObjectURL(url);
          next.delete(id);
          changed = true;
        }
      }
      for (const item of pending) {
        if (!next.has(item.id) && isImageAttachment(item.file.type)) {
          next.set(item.id, URL.createObjectURL(item.file));
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [pending]);
  useEffect(
    () => () => {
      for (const url of previewUrlsRef.current.values()) URL.revokeObjectURL(url);
    },
    [],
  );

  const messages = thread?.messages ?? [];
  const lastMessageId = messages.at(-1)?._id ?? null;

  // Auto-scroll on new messages while the user is at (or near) the bottom.
  useEffect(() => {
    if (!scrollerRef.current || !stickToBottomRef.current) return;
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [lastMessageId, chat.selectedId, thread?.loading]);

  // Transient highlight for reply/forward click-through (PRD). A target older than the loaded
  // window pages backwards (bounded) until it appears; a target that no longer exists clears the
  // highlight with a toast instead of staying silently armed.
  const { highlightMessageId, setHighlightMessageId } = chat;
  const highlightFetchBackRef = useRef(0);
  const scrolledForHighlightRef = useRef<string | null>(null);
  useEffect(() => {
    highlightFetchBackRef.current = 0;
    scrolledForHighlightRef.current = null;
  }, [highlightMessageId]);
  useEffect(() => {
    if (!highlightMessageId) return undefined;
    // Scroll + arm the flash ONCE per target; live appends must not re-yank the scroll position
    // or restart the timer while the highlight is showing.
    if (scrolledForHighlightRef.current === highlightMessageId) return undefined;
    const element = document.getElementById(`portal-chat-msg-${highlightMessageId}`);
    if (element) {
      scrolledForHighlightRef.current = highlightMessageId;
      element.scrollIntoView({ block: "center", behavior: "smooth" });
      element.classList.add("bg-[#fff8dd]");
      const timer = setTimeout(() => {
        element.classList.remove("bg-[#fff8dd]");
        setHighlightMessageId(null);
      }, HIGHLIGHT_FLASH_MS);
      return () => clearTimeout(timer);
    }
    if (thread && !thread.loading && thread.hasMore && highlightFetchBackRef.current < HIGHLIGHT_FETCH_BACK_MAX_PAGES) {
      highlightFetchBackRef.current += 1;
      void chat.loadOlderMessages();
    } else if (thread && !thread.loading) {
      setHighlightMessageId(null);
      chat.showToast(t("This message is no longer available"), false);
    }
    return undefined;
    // eslint-disable-next-line react-hooks-ext/exhaustive-deps -- loadOlderMessages/showToast are stable core callbacks; messages.length re-arms the search per fetched page
  }, [highlightMessageId, setHighlightMessageId, messages.length, thread?.loading, thread?.hasMore]);

  const memberById = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of thread?.members ?? []) map.set(String(member._id), member.fullName);
    return map;
  }, [thread?.members]);

  const readUpTo = useMemo(
    () => everyoneReadUpTo(thread?.members ?? [], thread?.memberReadDates ?? [], chat.userId),
    [thread?.members, thread?.memberReadDates, chat.userId],
  );

  if (!conversation) {
    // PRD §4.16: distinct copy for the unread filter, a brand-new portal, and a plain no-pick.
    const nothingExists = !chat.conversations.length;
    return (
      <div className="flex min-w-0 flex-1 flex-col">
        {chat.unreadOnly ? (
          <ThreadEmpty
            title={t("No unread messages")}
            body={t("Nothing here is waiting on you. Switch off Unread to reopen your conversations.")}
          />
        ) : nothingExists ? (
          <ThreadEmpty
            title={t("No conversation created yet")}
            body={t("Pick a way to start on the left. Once a conversation exists, it opens here.")}
          />
        ) : (
          <ThreadEmpty title={t("Select a chat to start messaging")} />
        )}
      </div>
    );
  }

  // The open conversation stays SELECTED across view switches, but it only renders while it is
  // part of the current view (type scope, hidden rule, unread filter — never the search).
  const conversationInView = listModel.inView.some((c) => c._id === conversation._id);
  if (!conversationInView) {
    if (listModel.inView.length === 0) {
      return (
        <div className="flex min-w-0 flex-1 flex-col">
          {chat.unreadOnly ? (
            <ThreadEmpty
              title={t("No unread messages")}
              body={t("Nothing here is waiting on you. Switch off Unread to reopen your conversations.")}
            />
          ) : (
            <ThreadEmpty
              title={
                chat.scope === "direct"
                  ? t("No direct chats yet")
                  : chat.scope === "group"
                    ? t("No group chats yet")
                    : chat.scope === "project"
                      ? t("No project chats yet")
                      : t("No conversation created yet")
              }
              body={t("Pick a way to start on the left. Once a conversation exists, it opens here.")}
            />
          )}
        </div>
      );
    }
    return (
      <div className="flex min-w-0 flex-1 flex-col">
        <ThreadEmpty
          title={t("Select a chat to begin")}
          body={t("The conversation you had open is not in {{view}}. Choose one from the left to carry on.", {
            view: listModel.scopeLabel,
          })}
        />
      </div>
    );
  }

  const subtitle = conversationSubtitle(conversation, t);
  const members = thread?.members ?? [];
  const memberReadDates = thread?.memberReadDates ?? [];

  /** Own-message receipt chip: ✓ until someone reads, ✓✓ N after — click opens Message details. */
  const renderReceipt = (message: ChatMessage, tone: "onBlue" | "light") => {
    const tick = tickStateFor(message, chat.userId, readUpTo);
    if (!tick) return null;
    const readers = readerCountFor(message, members, memberReadDates, chat.userId);
    const color =
      tone === "onBlue"
        ? readers > 0
          ? "text-white"
          : "text-white/70"
        : readers > 0
          ? "text-[#4794BA]"
          : "text-[#9AA5AD]";
    return (
      <button
        type="button"
        title={t("Message details")}
        onClick={(e) => {
          e.stopPropagation();
          chat.openReceiptsDialog(message);
        }}
        className={`flex items-center gap-0.5 text-[0.65rem] font-bold ${color}`}
      >
        <span>{readers > 0 ? "✓✓" : "✓"}</span>
        {readers > 0 && <span>{readers}</span>}
      </button>
    );
  };

  const renderFooter = (message: ChatMessage, own: boolean, tone: "onBlue" | "light") => (
    <div
      className={`mt-1 flex items-center justify-end gap-1.5 text-[0.65rem] ${
        tone === "onBlue" ? "text-white/80" : "text-[#6B757D]"
      }`}
    >
      {message.editedDate && !message.deleted && <span>{t("edited")}</span>}
      <span>{messageTime(message.createdDate)}</span>
      {own && renderReceipt(message, tone)}
    </div>
  );

  const renderAttachmentCard = (
    message: ChatMessage,
    attachment: MessageAttachment,
    own: boolean,
    withFooter: boolean,
  ) => {
    const name = attachment.attachmentsData.name;
    const image = isImageAttachment(attachment.attachmentsData.type);
    const size = attachment.size !== undefined ? formatFileSize(attachment.size) : null;
    const link = attachment.downloadLink;
    const removeButton =
      own && !message.deleted ? (
        <span
          role="button"
          tabIndex={-1}
          title={t("Remove attachment")}
          className="absolute end-1.5 top-1.5 hidden h-5 w-5 items-center justify-center rounded-full bg-white/90 shadow-xs group-hover/card:flex"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void chat.deleteAttachment(message._id, attachment.awsKey);
          }}
          onKeyDown={() => undefined}
        >
          <IconComponent name="CrossIcon" width="0.5rem" height="0.5rem" />
        </span>
      ) : null;
    if (image) {
      return (
        <div
          key={attachment.awsKey}
          className="group/card relative overflow-hidden rounded-[0.625rem] border border-[#DDE3E8] bg-white"
          style={{ width: ATTACHMENT_CARD_WIDTH }}
        >
          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            className="block border-b border-[#EEF2F5] bg-[#F7F9FB]"
            style={{ height: ATTACHMENT_PREVIEW_HEIGHT }}
          >
            {link ? (
              <img src={link} alt={name} loading="lazy" className="h-full w-full object-cover" />
            ) : (
              <span className="flex h-full items-center justify-center text-xs text-[#9AA5AD]">
                {t("Image preview")}
              </span>
            )}
          </a>
          <div className="px-3 pb-2 pt-2">
            <div className="truncate text-xs font-semibold text-[#1C2228]">{name}</div>
            <div className="mt-0.5 text-[0.6875rem] text-[#6B757D]">
              {size ? `${size} · ` : ""}
              {t("Click to open")}
            </div>
            {withFooter && renderFooter(message, own, "light")}
          </div>
          {removeButton}
        </div>
      );
    }
    return (
      <div
        key={attachment.awsKey}
        className="group/card relative rounded-[0.625rem] border border-[#DDE3E8] bg-white px-3 py-2.5"
        style={{ width: ATTACHMENT_CARD_WIDTH }}
      >
        <a href={link} target="_blank" rel="noreferrer" className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#E4F1F9]">
            <IconComponent name="attachFile" width="1rem" height="1rem" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs font-semibold text-[#1C2228]">{name}</span>
            <span className="block text-[0.6875rem] uppercase tracking-[0.08em] text-[#6B757D]">
              {t("File")}
              {size ? ` · ${size}` : ""}
            </span>
          </span>
          <IconComponent name="DownloadIcon" width="1rem" height="1rem" />
        </a>
        {withFooter && renderFooter(message, own, "light")}
        {removeButton}
      </div>
    );
  };

  const renderMessage = (message: ChatMessage, index: number) => {
    const previous = index > 0 ? messages[index - 1] : undefined;
    const own = String(message.senderId) === chat.userId;
    const showDay = showDaySeparator(message, previous);
    const grouped = isGroupedWithPrevious(message, previous);

    const daySeparator = showDay && (
      <div className="my-3 flex justify-center">
        <span className="rounded-full bg-white px-3 py-0.5 text-xs font-semibold text-[#6B757D] shadow-2xs">
          {dayLabel(message.createdDate, t("Today"), t("Yesterday"))}
        </span>
      </div>
    );

    if (message.kind === "system") {
      return (
        <div key={message._id}>
          {daySeparator}
          <SystemLine message={message} selfId={chat.userId} />
        </div>
      );
    }

    const forward = message.forwardOf;
    const reply = message.replyTo;
    const attachments = message.attachments ?? [];
    const bubbleShown = message.deleted || Boolean(message.text) || forward !== undefined;
    const tone = own ? "onBlue" : "light";
    const bubbleShape = own
      ? "rounded-[0.625rem_0.25rem_0.625rem_0.625rem] bg-[#4794BA] text-white"
      : "rounded-[0.25rem_0.625rem_0.625rem_0.625rem] bg-[#E4F1F9] text-[#1C2228]";

    const openForwardSource = () => {
      if (!forward || !forward.sourceAccessible) return;
      // PRD §4.4: a deleted source still navigates — the viewer lands on the source conversation
      // with a "no longer available" note instead of a highlight.
      if (forward.sourceDeleted) {
        chat.selectConversation(String(forward.conversationId));
        chat.showToast(t("This message is no longer available"), false);
      } else {
        chat.jumpToMessage(String(forward.conversationId), String(forward.messageId));
      }
    };

    const avatar = grouped ? (
      <span className="w-8 shrink-0" />
    ) : (
      <UserNameTagComponent showAvatarOnly id={own ? chat.userId : String(message.senderId)} size="XS" isCircle />
    );

    return (
      <div
        key={message._id}
        id={`portal-chat-msg-${message._id}`}
        className="rounded-xs transition-colors duration-500"
      >
        {daySeparator}
        <div
          className={`group flex items-start gap-2.5 px-4 ${grouped ? "mt-0.5" : "mt-3"} ${own ? "justify-end" : ""}`}
        >
          {!own && avatar}
          <div className={`flex min-w-0 max-w-[78%] flex-col ${own ? "items-end" : "items-start"}`}>
            {!grouped && (
              <div className="mb-1 text-[0.8125rem] font-bold text-[#1C2228]">
                {own ? t("You") : message.senderName}
              </div>
            )}
            {reply && !message.deleted && (
              <div
                role="button"
                tabIndex={0}
                onClick={() => chat.jumpToMessage(conversation._id, String(reply.messageId))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") chat.jumpToMessage(conversation._id, String(reply.messageId));
                }}
                className="mb-1.5 max-w-full cursor-pointer rounded-md border-s-2 border-[#4794BA] bg-white px-2.5 py-1.5 text-xs"
              >
                <div className="text-[0.6875rem] font-bold text-[#1F5F80]">{reply.senderName}</div>
                <div className="line-clamp-2 text-[#6B757D]">
                  {reply.deleted ? t("This message is no longer available") : reply.text || t("Attachment")}
                </div>
              </div>
            )}
            <div className={`flex max-w-full items-center gap-2.5 ${own ? "flex-row-reverse" : ""}`}>
              <div className={`flex min-w-0 flex-col gap-1.5 ${own ? "items-end" : "items-start"}`}>
                {bubbleShown && (
                  <div
                    className={`min-w-0 max-w-full px-3 pb-1.5 pt-2 text-[0.84375rem] leading-relaxed ${bubbleShape}`}
                  >
                    {message.deleted ? (
                      <span className="italic opacity-70">{t("This message was deleted")}</span>
                    ) : forward ? (
                      <>
                        {message.text && (
                          <span className="mb-2 block whitespace-pre-wrap wrap-break-word">{message.text}</span>
                        )}
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={openForwardSource}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") openForwardSource();
                          }}
                          className={`block w-full rounded-[0.4375rem] border border-[#DDE3E8] bg-white px-3 py-2 text-start text-[#1C2228] ${
                            forward.sourceAccessible ? "cursor-pointer hover:border-[#6BBBE3]" : ""
                          }`}
                        >
                          {forward.note && (
                            <div className="mb-1.5 border-b border-[#EEF2F5] pb-1.5 text-xs">
                              <span className="font-semibold">{forward.noteAuthorName}: </span>
                              <span className="whitespace-pre-wrap wrap-break-word">{forward.note}</span>
                            </div>
                          )}
                          <div className="mb-1 flex items-center gap-1.5">
                            <UserNameTagComponent showAvatarOnly id={String(forward.senderId)} size="XXS" isCircle />
                            <span className="text-xs font-bold">{forward.senderName}</span>
                          </div>
                          <div className="whitespace-pre-wrap wrap-break-word border-s-2 border-[#6BBBE3] ps-2.5 text-[0.8125rem] leading-relaxed text-[#4A545C]">
                            {forward.text}
                          </div>
                          {forward.attachments?.map((attachment) => (
                            <div
                              key={attachment.awsKey}
                              className="mt-1 flex items-center gap-1 text-xs text-[#2F7AA0]"
                            >
                              <IconComponent name="attachFile" width="0.7rem" height="0.7rem" />
                              <span className="truncate">{attachment.attachmentsData.name}</span>
                            </div>
                          ))}
                          <div className="mt-1.5 flex items-center gap-1 border-t border-[#EEF2F5] pt-1.5 text-[0.6875rem] text-[#6B757D]">
                            <IconComponent name="ForwardArrow" width="0.75rem" height="0.75rem" />
                            {forward.sourceAccessible
                              ? t("Forwarded from {{name}}", { name: forward.sourceName ?? "" })
                              : t("Forwarded from a private chat")}
                          </div>
                        </div>
                      </>
                    ) : (
                      <span className="whitespace-pre-wrap wrap-break-word">{message.text}</span>
                    )}
                    {renderFooter(message, own, tone)}
                  </div>
                )}
                {!message.deleted &&
                  attachments.map((attachment, attachmentIndex) =>
                    renderAttachmentCard(
                      message,
                      attachment,
                      own,
                      !bubbleShown && attachmentIndex === attachments.length - 1,
                    ),
                  )}
              </div>
              {!message.deleted && (
                <span className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100">
                  <button
                    type="button"
                    title={t("Message actions")}
                    onClick={(e) => setMenuFor({ message, anchor: e.currentTarget })}
                    className="flex h-6.5 w-6.5 items-center justify-center rounded-[0.4375rem] border border-[#EEF2F5] bg-white shadow-xs hover:border-[#DDE3E8] hover:bg-[#EEF2F5]"
                  >
                    <IconComponent name="threedots" width="0.85rem" height="0.85rem" />
                  </button>
                </span>
              )}
            </div>
            {!message.deleted && (message.reactions?.length ?? 0) > 0 && (
              <div className={`mt-1 flex max-w-full flex-wrap gap-1 ${own ? "justify-end" : ""}`}>
                {(message.reactions ?? []).map((reaction) => {
                  const mine = reaction.users.some((u) => String(u) === chat.userId);
                  const names = reaction.users
                    .map((u) => (String(u) === chat.userId ? t("You") : (memberById.get(String(u)) ?? "")))
                    .filter(Boolean)
                    .join(", ");
                  return (
                    <button
                      key={reaction.emoji}
                      type="button"
                      title={names}
                      onClick={() => {
                        if (mine) void chat.toggleReaction(message._id, reaction.emoji);
                        else chat.openReactionsDialog(message);
                      }}
                      className={`rounded-full border px-1.5 py-0.5 text-xs ${
                        mine ? "border-[#6BBBE3] bg-[#E4F1F9]" : "border-[#DDE3E8] bg-white"
                      }`}
                    >
                      {reaction.emoji} {reaction.users.length}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {own && avatar}
        </div>
      </div>
    );
  };

  const renderStagedChip = (item: PendingAttachment<File>) => {
    const preview = previewUrls.get(item.id);
    return (
      <span
        key={item.id}
        className={`flex h-7.5 max-w-[14rem] items-center gap-2 rounded-full border bg-white pe-1 ps-1 ${
          item.status === "error" ? "border-[#C4342A]" : "border-[#DDE3E8]"
        }`}
      >
        {preview ? (
          <img src={preview} alt="" className="h-5.5 w-5.5 rounded-full object-cover" />
        ) : (
          <IconComponent name="attachFile" width="0.8rem" height="0.8rem" className="ms-1" />
        )}
        <span className="min-w-0 flex-1 truncate text-xs text-[#1C2228]">{item.file.name}</span>
        {item.status === "uploading" && (
          <span className="whitespace-nowrap text-[0.6875rem] font-semibold text-[#1F5F80]">{t("Uploading…")}</span>
        )}
        {item.status === "error" && (
          <button
            type="button"
            className="text-[0.6875rem] font-semibold text-[#C4342A]"
            onClick={() => void retryUpload(item)}
          >
            {t("Retry")}
          </button>
        )}
        <button
          type="button"
          title={t("Remove")}
          onClick={() => removePending(item.id)}
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#EEF2F5] hover:bg-[#FBEAE8]"
        >
          <IconComponent name="CrossIcon" width="0.5rem" height="0.5rem" />
        </button>
      </span>
    );
  };

  const typingLine = typingLineText(chat.typingUsers, t);
  const intent = chat.composerIntent;
  const mentionCandidates = members.filter((member) => String(member._id) !== chat.userId);

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      {/* Thread header */}
      <div className="flex items-center justify-between gap-3 border-b border-[#EEF2F5] px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <ConversationAvatar conversation={conversation} size="header" />
          <div className="min-w-0">
            <div className="truncate text-sm font-bold text-[#1C2228]">{conversation.name}</div>
            {subtitle && <div className="truncate text-xs text-[#6B757D]">{subtitle}</div>}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {conversation.type !== "direct" && (
            <button
              type="button"
              title={t("Members")}
              onClick={() => setShowMembers(true)}
              className="flex h-7.5 items-center gap-1.5 rounded-[0.4375rem] border border-[#DDE3E8] px-2.5 text-xs font-semibold text-[#4A545C] hover:border-[#6BBBE3] hover:text-[#1F5F80]"
            >
              <IconComponent name="usersIcon" width="0.9rem" height="0.9rem" />
              {conversation.memberCount}
            </button>
          )}
          <IconComponent
            name={conversation.muted ? "SpeakerOff" : "SpeakerOn"}
            width="1.1rem"
            height="1.1rem"
            toolTipContent={conversation.muted ? t("Unmute") : t("Mute")}
            onClick={() => {
              void chat.updateConversationState(conversation._id, { muted: !conversation.muted });
              chat.showToast(conversation.muted ? t("Chat unmuted") : t("Chat muted"));
            }}
          />
          <IconComponent
            name="attachFile"
            width="1rem"
            height="1rem"
            toolTipContent={t("View attachments")}
            onClick={() => setShowAttachments(true)}
          />
          <span
            role="button"
            tabIndex={-1}
            title={t("More")}
            className="rounded-md p-1 hover:bg-[#EEF2F5]"
            onClick={(e) => setThreadMenuAnchor(e.currentTarget)}
            onKeyDown={() => undefined}
          >
            <IconComponent name="threedots" width="1rem" height="1rem" className="rotate-90" />
          </span>
        </div>
      </div>
      {threadMenuAnchor && (
        <MenuComponent anchorEl={threadMenuAnchor} open onClose={() => setThreadMenuAnchor(null)} width="14rem">
          <MenuItemComponent
            onClick={() => {
              setThreadMenuAnchor(null);
              setHideCandidate(true);
            }}
          >
            {t("Hide conversation")}
          </MenuItemComponent>
          {conversation.type === "group" && (
            <MenuItemComponent
              onClick={() => {
                setThreadMenuAnchor(null);
                setShowRename(true);
              }}
            >
              {t("Rename conversation")}
            </MenuItemComponent>
          )}
          <MenuItemComponent
            onClick={() => {
              setThreadMenuAnchor(null);
              setShowAttachments(true);
            }}
          >
            {t("View attachments")}
          </MenuItemComponent>
          {conversation.type !== "direct" && (
            <MenuItemComponent
              onClick={() => {
                setThreadMenuAnchor(null);
                setShowMembers(true);
              }}
            >
              {t("Members")}
            </MenuItemComponent>
          )}
        </MenuComponent>
      )}

      {/* Messages */}
      <div
        ref={scrollerRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        }}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-[#F7F9FB] pb-3 pt-1"
      >
        {thread?.loading && <div className="p-4 text-sm text-[#6B757D]">{t("Loading")}…</div>}
        {thread && !thread.loading && thread.error && (
          <ThreadEmpty title={t("Couldn't load this conversation")} body={t("Check your connection and try again.")} />
        )}
        {thread && !thread.loading && thread.error && (
          <div className="flex justify-center pb-4">
            <button
              type="button"
              className="rounded-[0.4375rem] border border-[#DDE3E8] bg-white px-3 py-1 text-xs font-semibold text-[#1C2228] hover:border-[#C3CCD3]"
              onClick={() => chat.selectConversation(conversation._id)}
            >
              {t("Retry")}
            </button>
          </div>
        )}
        {thread && !thread.loading && thread.hasMore && (
          <div className="flex justify-center pt-2">
            <button
              type="button"
              className="rounded-full border border-[#DDE3E8] bg-white px-3 py-0.5 text-xs text-[#1F5F80]"
              onClick={() => void chat.loadOlderMessages()}
            >
              {t("Load earlier messages")}
            </button>
          </div>
        )}
        {thread && !thread.loading && !thread.error && !messages.length && (
          <ThreadEmpty
            title={t("No messages yet")}
            body={
              conversation.type === "space"
                ? t(
                    "Nothing has been posted in the {{name}} Space yet. Start the conversation and everyone in this Space will see it.",
                    { name: conversation.name },
                  )
                : conversation.type === "direct"
                  ? t("Say hello to {{name}} to start this conversation.", {
                      name: conversation.name.split(" ")[0] ?? conversation.name,
                    })
                  : t("No one has posted in {{name}} yet. Start the conversation.", { name: conversation.name })
            }
          />
        )}
        {messages.map((message, index) => renderMessage(message, index))}
        {failedSend && failedSend.conversationId === conversation._id && (
          <div className="mt-3 flex justify-end px-4">
            <div className="max-w-[78%] rounded-[0.625rem] border border-[#F0B6B1] bg-[#FBEAE8] px-3 py-2 text-[0.84375rem]">
              {failedSend.text && <span className="whitespace-pre-wrap wrap-break-word">{failedSend.text}</span>}
              {failedSend.attachments.map((attachment) => (
                <div key={attachment.key} className="flex items-center gap-1 text-xs text-[#6B757D]">
                  <IconComponent name="attachFile" width="0.7rem" height="0.7rem" />
                  {attachment.name}
                </div>
              ))}
              <div className="mt-1 flex items-center justify-end gap-2 text-xs">
                <span className="text-[#C4342A]">{t("Not sent.")}</span>
                <button type="button" className="font-semibold text-[#1F5F80]" onClick={() => void retryFailedSend()}>
                  {t("Retry")}
                </button>
                <button type="button" className="text-[#6B757D]" onClick={dismissFailedSend}>
                  {t("Remove")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-[#EEF2F5] px-4 pb-3 pt-2">
        {!chat.connected && (
          <div className="mb-2 rounded-md bg-[#FBF0DE] px-2.5 py-1 text-xs text-[#C67C1A]">
            {t("You're offline. New messages will appear and sending is paused until you're back.")}
          </div>
        )}
        <div className="mb-1.5 flex h-4 items-center gap-2 text-[0.7rem] text-[#6B757D]">
          {typingLine && (
            <>
              <span className="flex items-center gap-0.5">
                <span className="h-1 w-1 animate-bounce rounded-full bg-[#4794BA]" />
                <span className="h-1 w-1 animate-bounce rounded-full bg-[#4794BA] [animation-delay:150ms]" />
                <span className="h-1 w-1 animate-bounce rounded-full bg-[#4794BA] [animation-delay:300ms]" />
              </span>
              <span>{typingLine}</span>
            </>
          )}
        </div>
        {intent && (
          <div className="mb-2 flex items-center justify-between gap-2 rounded-md border-s-2 border-[#4794BA] bg-[#F4FAFD] px-2.5 py-1.5 text-xs">
            <div className="min-w-0">
              <div className="font-bold text-[#1F5F80]">
                {t("Replying to {{name}}", { name: intent.message.senderName })}
              </div>
              <div className="truncate text-[#6B757D]">{intent.message.text ?? t("Attachment")}</div>
            </div>
            <span
              role="button"
              tabIndex={0}
              title={t("Cancel reply")}
              onClick={() => chat.setComposerIntent(null)}
              onKeyDown={() => undefined}
            >
              <IconComponent name="CrossIcon" width="0.7rem" height="0.7rem" />
            </span>
          </div>
        )}
        {pending.length > 0 && (
          <div className="mb-2 rounded-[0.5625rem] border border-[#DDE3E8] bg-[#F7F9FB] px-3 py-2.5">
            <div className="mb-2 flex items-center gap-2">
              <span className="flex-1 text-[0.6875rem] font-bold uppercase tracking-[0.1em] text-[#9AA5AD]">
                {pending.length === 1
                  ? t("One attachment ready")
                  : t("{{count}} attachments ready", { count: pending.length })}
              </span>
              <button
                type="button"
                onClick={removeAllPending}
                className="text-[0.7rem] font-semibold text-[#6B757D] hover:text-[#C4342A]"
              >
                {t("Remove all")}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">{pending.map(renderStagedChip)}</div>
          </div>
        )}
        <div
          className={`flex items-center gap-2 rounded-[0.5625rem] border bg-white py-1.5 pe-1.5 ps-2.5 ${
            composerFocused ? "border-[#4794BA]" : "border-[#DDE3E8]"
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              void addFiles(Array.from(e.target.files ?? []));
              e.target.value = "";
            }}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => {
              void addFiles(Array.from(e.target.files ?? []));
              e.target.value = "";
            }}
          />
          <button
            type="button"
            title={t("Attach")}
            onClick={(e) => setAttachMenuAnchor(e.currentTarget)}
            className="flex h-7.5 w-7.5 shrink-0 items-center justify-center rounded-md hover:bg-[#EEF2F5]"
          >
            <IconComponent name="attachFile" width="1.05rem" height="1.05rem" />
          </button>
          {mentionCandidates.length > 0 && (
            <button
              type="button"
              title={t("Mention")}
              onClick={() => {
                insertText("@");
                setShowMentionPicker(true);
                textareaRef.current?.focus();
              }}
              className="flex h-7.5 w-7.5 shrink-0 items-center justify-center rounded-md text-base font-semibold text-[#4A545C] hover:bg-[#EEF2F5]"
            >
              @
            </button>
          )}
          <div className="relative min-w-0 flex-1">
            <textarea
              ref={textareaRef}
              value={text}
              disabled={!chat.connected}
              rows={Math.min(5, Math.max(1, text.split("\n").length))}
              maxLength={MESSAGE_TEXT_MAX_LENGTH}
              placeholder={pending.length ? t("Add a message about these files…") : t("Write a message")}
              onFocus={() => setComposerFocused(true)}
              onBlur={() => setComposerFocused(false)}
              onChange={(e) => handleTextChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
                if (e.key === "Escape") {
                  if (showMentionPicker) setShowMentionPicker(false);
                  else chat.setComposerIntent(null);
                }
              }}
              className="w-full resize-none border-none bg-transparent py-1 text-[0.84375rem] text-[#1C2228] outline-hidden placeholder:text-[#9AA5AD]"
            />
            {text.length > MESSAGE_TEXT_MAX_LENGTH - 200 && (
              <span className="absolute -top-4 end-0 text-[0.6rem] text-[#9AA5AD]">
                {text.length}/{MESSAGE_TEXT_MAX_LENGTH}
              </span>
            )}
            {showMentionPicker && (
              <div className="absolute bottom-full start-0 z-10 mb-3 w-64 rounded-[0.5625rem] border border-[#EEF2F5] bg-white p-1.5 shadow-lg">
                <div className="px-2 pb-1 pt-1.5 text-[0.625rem] font-bold uppercase tracking-[0.12em] text-[#9AA5AD]">
                  {t("Mention")}
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {mentionCandidates.map((member) => (
                    <button
                      key={String(member._id)}
                      type="button"
                      className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-start hover:bg-[#EEF2F5]"
                      onClick={() => {
                        insertMention({ _id: String(member._id), fullName: member.fullName });
                        textareaRef.current?.focus();
                      }}
                    >
                      <UserNameTagComponent showAvatarOnly id={String(member._id)} size="XS" isCircle />
                      <span className="min-w-0">
                        <span className="block truncate text-[0.8125rem] font-semibold text-[#1C2228]">
                          {member.fullName}
                        </span>
                        {member.designation && (
                          <span className="block truncate text-[0.6875rem] text-[#6B757D]">{member.designation}</span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button
            type="button"
            disabled={sending || uploading || failed || !chat.connected}
            onClick={() => void handleSend()}
            className="flex h-7.5 w-7.5 shrink-0 items-center justify-center rounded-[0.4375rem] bg-[#4794BA] hover:bg-[#2F7AA0] disabled:cursor-not-allowed disabled:opacity-40"
            title={
              !chat.connected
                ? t("Reconnecting…")
                : uploading
                  ? t("Waiting for uploads to finish")
                  : failed
                    ? t("Retry or remove the failed attachment first")
                    : t("Send")
            }
          >
            <IconComponent name="send" width="0.95rem" height="0.95rem" className="brightness-0 invert" />
          </button>
        </div>
      </div>

      {/* Attach menu: camera capture or device files */}
      {attachMenuAnchor && (
        <MenuComponent
          anchorEl={attachMenuAnchor}
          open
          onClose={() => setAttachMenuAnchor(null)}
          width="13rem"
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <MenuItemComponent
            onClick={() => {
              setAttachMenuAnchor(null);
              cameraInputRef.current?.click();
            }}
          >
            <span className="flex items-center gap-2.5">
              <IconComponent name="Camera" width="1rem" height="1rem" />
              {t("Take Photo")}
            </span>
          </MenuItemComponent>
          <MenuItemComponent
            onClick={() => {
              setAttachMenuAnchor(null);
              fileInputRef.current?.click();
            }}
          >
            <span className="flex items-center gap-2.5">
              <IconComponent name="folder" width="1rem" height="1rem" />
              {t("Files from Device")}
            </span>
          </MenuItemComponent>
        </MenuComponent>
      )}

      {/* Message actions — opens beside the bubble (left of own, right of others) */}
      {menuFor && (
        <MenuComponent
          anchorEl={menuFor.anchor}
          open
          onClose={() => setMenuFor(null)}
          width="13rem"
          anchorOrigin={{
            vertical: "bottom",
            horizontal: String(menuFor.message.senderId) === chat.userId ? "right" : "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: String(menuFor.message.senderId) === chat.userId ? "right" : "left",
          }}
        >
          <MenuItemComponent
            onClick={() => {
              chat.setComposerIntent({ kind: "reply", message: menuFor.message });
              setMenuFor(null);
              textareaRef.current?.focus();
            }}
          >
            {t("Reply")}
          </MenuItemComponent>
          <MenuItemComponent
            onClick={() => {
              chat.openForwardDialog(menuFor.message);
              setMenuFor(null);
            }}
          >
            {t("Forward")}
          </MenuItemComponent>
          <MenuItemComponent
            onClick={() => {
              const { anchor, message } = menuFor;
              setMenuFor(null);
              setEmojiAnchor({ anchor, messageId: message._id });
            }}
          >
            {t("React")}
          </MenuItemComponent>
          {menuFor.message.text && (
            <MenuItemComponent
              onClick={() => {
                void navigator.clipboard.writeText(menuFor.message.text ?? "");
                setMenuFor(null);
              }}
            >
              {t("Copy text")}
            </MenuItemComponent>
          )}
          {canEditMessage(menuFor.message, chat.userId) && (
            <MenuItemComponent
              onClick={() => {
                chat.openEditDialog(menuFor.message);
                setMenuFor(null);
              }}
            >
              {t("Edit message")}
            </MenuItemComponent>
          )}
          {String(menuFor.message.senderId) === chat.userId && (
            <MenuItemComponent
              onClick={() => {
                void chat.deleteMessage(menuFor.message._id);
                setMenuFor(null);
              }}
            >
              {t("Delete")}
            </MenuItemComponent>
          )}
          {(menuFor.message.reactions?.length ?? 0) > 0 && (
            <MenuItemComponent
              onClick={() => {
                chat.openReactionsDialog(menuFor.message);
                setMenuFor(null);
              }}
            >
              {t("View reactions")}
            </MenuItemComponent>
          )}
          {/* Receipts are the sender's: only own messages carry Message details. */}
          {String(menuFor.message.senderId) === chat.userId && (
            <MenuItemComponent
              onClick={() => {
                chat.openReceiptsDialog(menuFor.message);
                setMenuFor(null);
              }}
            >
              {t("Message details")}
            </MenuItemComponent>
          )}
        </MenuComponent>
      )}

      {/* Emoji picker for a reaction (the composer has no emoji button in the design) */}
      {emojiAnchor && (
        <EmojiPicker
          anchorEl={emojiAnchor.anchor}
          onClose={() => setEmojiAnchor(null)}
          onPick={(emoji) => void chat.toggleReaction(emojiAnchor.messageId, emoji)}
        />
      )}

      {showMembers && <MembersDialog conversation={conversation} onClose={() => setShowMembers(false)} />}
      {showAttachments && <AttachmentsDialog conversation={conversation} onClose={() => setShowAttachments(false)} />}
      {showRename && <RenameDialog conversation={conversation} onClose={() => setShowRename(false)} />}
      {hideCandidate && <HideConfirmDialog conversation={conversation} onClose={() => setHideCandidate(false)} />}
    </div>
  );
}

export default ChatThreadPane;
